import { type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isTopmostSheet(sheet: HTMLElement | null): boolean {
  if (sheet === null) {
    return false;
  }
  const sheets = document.querySelectorAll(".sheet");
  if (sheets[sheets.length - 1] !== sheet) {
    return false;
  }
  return !Array.from(document.querySelectorAll("dialog[open]")).some(
    (dialog) => dialog.closest(".sheet") === null,
  );
}

export function BottomSheet({
  labelledBy,
  onClose,
  returnFocusTo,
  children,
}: {
  labelledBy: string;
  onClose: () => void;
  returnFocusTo?: HTMLElement | null;
  children: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous =
      returnFocusTo ??
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const node = panel.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? node)?.focus();
    return () => {
      if (previous?.isConnected) {
        previous.focus();
      }
    };
  }, [returnFocusTo]);

  useEffect(() => {
    function closeOnEscape(event: globalThis.KeyboardEvent) {
      if (event.key !== "Escape" || !isTopmostSheet(root.current)) {
        return;
      }
      event.stopPropagation();
      closeRef.current();
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== "Tab" || !panel.current) {
      return;
    }
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (!firstItem || !lastItem) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === firstItem || active === panel.current)) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && active === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  }

  return createPortal(
    <div ref={root} className="sheet">
      <button
        type="button"
        className="sheet__backdrop"
        aria-label="Cerrar"
        tabIndex={-1}
        onClick={() => closeRef.current()}
      />
      <dialog
        ref={panel}
        open
        className="sheet__panel"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="sheet__handle" aria-hidden="true" />
        {children}
      </dialog>
    </div>,
    document.body,
  );
}
